import { useEffect, useState } from "react";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Receipt } from "lucide-react";

const gbp = (n) => `£${(n || 0).toFixed(2)}`;

function QueryCard({ q, onDone }) {
  const [spend, setSpend] = useState("");
  const [note, setNote] = useState("");
  const resolve = async (decision) => {
    try { await api.post(`/admin/affiliate/queries/${q.id}/resolve`, { decision, spend: decision === "adjust" ? Number(spend) : null, note: note || null }); toast.success("Resolved"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const open = q.status === "queried";
  return (
    <div className="glass rounded-2xl p-4 space-y-2" data-testid={`ad-query-${q.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap"><div><div className="font-semibold">{q.ad_name} · {q.month}</div><div className="text-xs text-zinc-500">{q.brand_name} → {q.creator_name} · reported {gbp(q.amount)} × {q.pct}% = {gbp(q.commission)}</div></div>
        <a href={q.receipt_url} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 text-zinc-300 hover:text-white" data-testid={`ad-query-receipt-${q.id}`}><Receipt className="h-3.5 w-3.5" /> Brand receipt</a></div>
      <div className="text-sm text-amber-200 bg-amber-500/5 border border-amber-500/20 rounded-xl px-3 py-2">"{q.query?.reason}"</div>
      {open ? (
        <div className="flex gap-2 flex-wrap items-center">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to both sides (optional)" className="flex-1 min-w-[160px] h-8 bg-white/[0.02] border-white/10 text-white" data-testid={`ad-query-note-${q.id}`} />
          <Button size="sm" variant="outline" onClick={() => resolve("uphold")} className="rounded-full border-white/15 text-white h-8" data-testid={`ad-query-uphold-${q.id}`}>Keep as reported</Button>
          <Input type="number" value={spend} onChange={(e) => setSpend(e.target.value)} placeholder="Correct £" className="w-24 h-8 bg-white/[0.02] border-white/10 text-white" data-testid={`ad-query-spend-${q.id}`} />
          <Button size="sm" onClick={() => resolve("adjust")} disabled={!spend} className="rounded-full nexus-gradient text-white border-0 h-8" data-testid={`ad-query-adjust-${q.id}`}>Correct</Button>
          <Button size="sm" variant="ghost" onClick={() => resolve("void")} className="rounded-full text-red-300 h-8" data-testid={`ad-query-void-${q.id}`}>Remove</Button>
        </div>
      ) : <div className="text-xs text-zinc-500">Resolved: {q.resolution?.decision}{q.resolution?.note ? ` · ${q.resolution.note}` : ""}</div>}
    </div>
  );
}

export default function AdminAdQueries() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/affiliate/queries").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  return (
    <BrandShell>
      <SEO title="Ad spend queries · Admin" description="Review creator queries on reported ad spend." path="/admin/ad-queries" noIndex />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-4" data-testid="admin-ad-queries-page">
        <div><div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Admin</div><h1 className="font-display font-bold text-3xl tracking-tighter">Ad spend queries</h1>
          <p className="text-sm text-zinc-400 mt-1">Creators can query a brand's reported ad spend before it's invoiced. The payout stays on hold until you keep, correct or remove it.</p></div>
        {rows && rows.length === 0 && <div className="glass rounded-2xl p-6 text-sm text-zinc-500" data-testid="ad-queries-empty">No queries.</div>}
        {(rows || []).map((q) => <QueryCard key={q.id} q={q} onDone={load} />)}
      </div>
    </BrandShell>
  );
}
